/**
 * Shape of the advanced filter panel.
 *
 * This is the single source of truth for a queue's filters: it is what the
 * panel edits, what gets persisted, and what `buildInvoiceQuery` turns into
 * request parameters. Filtering itself happens on the server — the old app
 * filtered a page of already-fetched rows in the browser, so counts and
 * pagination disagreed with what the filter said.
 */
export const EMPTY_INVOICE_FILTERS = {
  reviewStatusIds: [],
  processingStatusIds: [],
  createdOnAfter: "",
  createdOnBefore: "",
  invoiceDateAfter: "",
  invoiceDateBefore: "",
  totalMin: "",
  totalMax: "",
  assignedToId: "",
};

export function cloneInvoiceFilters(filters = EMPTY_INVOICE_FILTERS) {
  return {
    ...EMPTY_INVOICE_FILTERS,
    ...filters,
    reviewStatusIds: [...(filters?.reviewStatusIds ?? [])],
    processingStatusIds: [...(filters?.processingStatusIds ?? [])],
  };
}

export function countActiveInvoiceFilters(filters) {
  if (!filters) return 0;

  let count =
    (filters.reviewStatusIds?.length ?? 0) + (filters.processingStatusIds?.length ?? 0);

  [
    "createdOnAfter",
    "createdOnBefore",
    "invoiceDateAfter",
    "invoiceDateBefore",
    "totalMin",
    "totalMax",
    "assignedToId",
  ].forEach((key) => {
    if (filters[key] !== "" && filters[key] != null) count += 1;
  });

  return count;
}

export function invoiceFiltersAreActive(filters) {
  return countActiveInvoiceFilters(filters) > 0;
}

/**
 * An invoice has one review status and one processing status, so two filters
 * on the same field have to agree. An empty list means that source does not
 * constrain the field. Two non-empty lists combine by intersection: Failed
 * and Under Review cannot both be true, and sending both ids makes the API
 * treat them as OR.
 *
 * A disjoint intersection is unsatisfiable. Dropping the ids would send no
 * status at all and return the unfiltered queue, so the flag stays set and
 * the list query is skipped.
 */
function intersectStatusIds(current = [], incoming = []) {
  if (incoming.length === 0) return { ids: [...current], unsatisfiable: false };
  if (current.length === 0) return { ids: [...incoming], unsatisfiable: false };

  const allowed = new Set(incoming.map(Number));
  const ids = current.filter((id) => allowed.has(Number(id)));
  return { ids, unsatisfiable: ids.length === 0 };
}

/** Merges a queue's fixed constraints with the chip and the filter panel. */
export function mergeInvoiceFilters(...sources) {
  return sources.reduce((merged, source) => {
    if (!source) return merged;

    const review = intersectStatusIds(merged.reviewStatusIds, source.reviewStatusIds);
    const processing = intersectStatusIds(
      merged.processingStatusIds,
      source.processingStatusIds,
    );

    return {
      ...merged,
      ...source,
      reviewStatusIds: review.ids,
      processingStatusIds: processing.ids,
      reviewStatusUnsatisfiable: Boolean(merged.reviewStatusUnsatisfiable || review.unsatisfiable),
      processingStatusUnsatisfiable: Boolean(
        merged.processingStatusUnsatisfiable || processing.unsatisfiable,
      ),
    };
  }, cloneInvoiceFilters());
}

export function invoiceFiltersConflict(filters) {
  return Boolean(filters?.reviewStatusUnsatisfiable || filters?.processingStatusUnsatisfiable);
}
